import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type NotificationVariant = 'warning' | 'error' | 'success' | 'info';

export interface NotificationType {
  type: NotificationVariant;
  title?: string;
  description?: string;
  sticky?: boolean;
  closeable?: boolean;
}

interface NotificationContextType {
  notification: NotificationType | null;
  setNotification: (notification: NotificationType) => void;
  dismissNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notification, setNotificationState] = useState<NotificationType | null>(null);

  const setNotification = useCallback((newNotification: NotificationType) => {
    setNotificationState({
      ...newNotification,
      closeable: newNotification.closeable !== false,
    });

    // Auto-dismiss after 5 seconds for non-sticky notifications
    if (!newNotification.sticky) {
      setTimeout(() => {
        setNotificationState(null);
      }, 5000);
    }
  }, []);

  const dismissNotification = useCallback(() => {
    setNotificationState(null);
  }, []);

  return (
    <NotificationContext.Provider value={{ notification, setNotification, dismissNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default useNotifications;
